import React from 'react';
import { Activity } from 'lucide-react';

export interface LoadingSpinnerProps {
  message?: string;
  subtitle?: string;
  className?: string;
  pillText?: string;
  compact?: boolean;
}

/**
 * Institutional Market Intelligence Desk Loader
 * Theme-aware, glassmorphic loading terminal with live telemetry indicators.
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message = '',
  subtitle = '',
  className = 'loader-container',
  pillText = 'MARKET INTELLIGENCE DESK',
  compact = false,
}) => {
  const displayTitle = message || 'Loading Market Intelligence Desk';
  const displaySubtitle = subtitle || 'Synthesizing market breadth, price-volume action, and institutional flows.';

  return (
    <div
      className={`loader-container ${className} ${compact ? 'loader-container--compact' : ''}`}
      role="status"
      aria-live="polite"
    >
      {/* Soft Ambient Background Glow */}
      <div className="ns-loader-ambient-glow" aria-hidden="true" />

      <div className={`loader-card ns-terminal-loader-card ${compact ? 'ns-terminal-loader-card--compact' : ''}`}>
        {/* Live Telemetry Pill */}
        <div className="ns-loader-telemetry-pill">
          <span className="ns-loader-pulse-dot" />
          <span className="ns-loader-pill-label">{pillText}</span>
        </div>

        {/* Central Orbital Radar Visual */}
        <div className="loader-visual ns-loader-visual" aria-hidden="true">
          <div className="ns-loader-orbital-ring ns-loader-orbital-ring--outer" />
          <div className="ns-loader-orbital-ring ns-loader-orbital-ring--inner" />
          <div className="loader-badge ns-loader-center-badge">
            <Activity size={20} className="ns-loader-badge-icon" />
          </div>
        </div>

        {/* Header & Status Copy */}
        <div className="loader-copy ns-loader-copy">
          <strong className="loader-title ns-loader-title">{displayTitle}</strong>
          <span className="loader-subtitle ns-loader-subtitle">{displaySubtitle}</span>
        </div>

        {/* Indeterminate Financial Progress Runner */}
        <div className="ns-loader-progress-track" aria-hidden="true">
          <div className="ns-loader-progress-bar" />
        </div>

        {/* Institutional System Status Footer */}
        <div className="ns-loader-footer">
          <span className="ns-loader-footer-item">NSE CASH EQUITIES</span>
          <span className="ns-loader-footer-sep">·</span>
          <span className="ns-loader-footer-item">MULTI-TIMEFRAME BREADTH</span>
          <span className="ns-loader-footer-sep">·</span>
          <span className="ns-loader-footer-item">INSTITUTIONAL FLOW</span>
        </div>
      </div>
    </div>
  );
};

export default LoadingSpinner;
